import { useEffect, useState } from 'react';
import {
  MAX_SUPPORTED_ZOOM,
  type BasemapId,
  type CompanySettings,
  type UpdateCompanySettings,
} from '@geotracker/contracts';
import { Card, CardHeader } from '@/components/ui/card';
import { ErrorState, Loading } from '@/components/ui/state';
import { useCompanySettings, useUpdateSettings } from '@/lib/api/settings';
import { useAuth } from '@/lib/auth/auth-store';
import { BASEMAPS } from '@/features/live/basemaps';
import { formatDuration } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Company settings.
 *
 * Two groups today, and they are the two an administrator actually has to
 * decide: what the map draws on, and where the line falls between "we knew" and
 * "we found out". Both are per-company because both are judgement calls that
 * differ by deployment — a city courier fleet and a rural survey crew disagree
 * about what counts as a late fix, and one of them would be looking at the
 * wrong colour all day under a single global constant.
 *
 * Writes are admin-only server side. A manager reaching this page sees the
 * values (they explain what the map is doing) with the controls disabled,
 * rather than a permission error after typing into a form.
 */
export function SettingsPage() {
  const query = useCompanySettings();
  const role = useAuth((s) => s.user?.role);
  const canEdit = role === 'super_admin' || role === 'company_admin';

  if (query.isPending) return <Loading label="Loading settings…" />;
  if (query.error) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (!query.data) return <Loading label="Loading settings…" />;

  return (
    <section className="mx-auto max-w-3xl space-y-4">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {canEdit
            ? 'These apply to everyone in your company.'
            : 'Read-only — your role cannot change company settings.'}
        </p>
      </header>

      <MapsCard settings={query.data} canEdit={canEdit} />
      <DeliveryCard settings={query.data} canEdit={canEdit} />
    </section>
  );
}

function MapsCard({ settings, canEdit }: { settings: CompanySettings; canEdit: boolean }) {
  const mutation = useUpdateSettings();

  const [key, setKey] = useState(settings.maps.googleMapsBrowserKey ?? '');
  const [reveal, setReveal] = useState(false);
  const [basemap, setBasemap] = useState<BasemapId>(settings.maps.defaultBasemap);
  const [maxZoom, setMaxZoom] = useState(settings.maps.maxZoom);
  const [language, setLanguage] = useState(settings.maps.labelLanguage);
  const [region, setRegion] = useState(settings.maps.labelRegion);

  // Re-seed when the server's copy changes under us — another admin saving, or
  // this form's own save returning the settled values.
  useEffect(() => {
    setKey(settings.maps.googleMapsBrowserKey ?? '');
    setBasemap(settings.maps.defaultBasemap);
    setMaxZoom(settings.maps.maxZoom);
    setLanguage(settings.maps.labelLanguage);
    setRegion(settings.maps.labelRegion);
  }, [settings.maps]);

  const save = () => {
    const patch: UpdateCompanySettings = {
      maps: {
        // Blank means "remove the key", which must reach the server as null
        // rather than as an empty string the schema would happily store and the
        // map would then treat as configured.
        googleMapsBrowserKey: key.trim() === '' ? null : key.trim(),
        defaultBasemap: basemap,
        maxZoom,
        labelLanguage: language.trim() || 'en',
        labelRegion: region.trim() || 'IN',
      },
    };
    mutation.mutate(patch);
  };

  const googleReady = key.trim() !== '';

  return (
    <Card>
      <CardHeader
        title="Map"
        hint="Basemap, zoom depth, and the Google Maps credentials for satellite and place names."
      />

      <div className="space-y-4 p-4">
        <Field
          label="Google Maps browser key"
          hint={
            <>
              A <strong>browser</strong> key from Google Cloud with the <strong>Map Tiles API</strong>{' '}
              enabled, restricted by HTTP referrer to this panel&rsquo;s domain. Leave blank to stay
              on OpenStreetMap and Esri imagery, which need no key and cost nothing. Google tiles
              are billed per session and per tile — check your quota before rolling this out to a
              room full of dispatchers.
            </>
          }
        >
          <div className="flex gap-2">
            <input
              type={reveal ? 'text' : 'password'}
              value={key}
              disabled={!canEdit}
              onChange={(e) => setKey(e.target.value)}
              placeholder="AIza…"
              autoComplete="off"
              spellCheck={false}
              className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono disabled:opacity-60"
            />
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              className="shrink-0 rounded-lg border border-border px-3 text-xs transition-colors hover:bg-muted"
            >
              {reveal ? 'Hide' : 'Show'}
            </button>
          </div>
        </Field>

        <Field
          label="Default basemap"
          hint="What a fresh session opens on. Each admin's own last choice is remembered in their browser and wins over this."
        >
          <select
            value={basemap}
            disabled={!canEdit}
            onChange={(e) => setBasemap(e.target.value as BasemapId)}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:opacity-60"
          >
            {BASEMAPS.map((b) => (
              <option key={b.id} value={b.id} disabled={b.needsGoogleKey && !googleReady}>
                {b.label}
                {b.needsGoogleKey && !googleReady ? ' — needs a Google key' : ''}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={`Maximum zoom — ${maxZoom}`}
          hint={
            <>
              Above a provider&rsquo;s own maximum (19 for OpenStreetMap, 21 for Google) the last
              real tile is upscaled rather than refetched. That buys magnification, not detail —
              which is exactly what is wanted when a dozen fixes land within a few metres of each
              other and the pins overlap.
            </>
          }
        >
          <input
            type="range"
            min={16}
            max={MAX_SUPPORTED_ZOOM}
            step={1}
            value={maxZoom}
            disabled={!canEdit}
            onChange={(e) => setMaxZoom(Number(e.target.value))}
            className="h-1 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary disabled:opacity-60"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Label language" hint="ISO code for Google's place labels, e.g. en, hi, kn.">
            <input
              value={language}
              disabled={!canEdit}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:opacity-60"
            />
          </Field>
          <Field
            label="Label region"
            hint="Two-letter region code. It decides how Google renders disputed borders and place names — for an India deployment this should stay IN."
          >
            <input
              value={region}
              disabled={!canEdit}
              onChange={(e) => setRegion(e.target.value.toUpperCase())}
              maxLength={4}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:opacity-60"
            />
          </Field>
        </div>

        <SaveRow
          canEdit={canEdit}
          pending={mutation.isPending}
          error={mutation.error}
          saved={mutation.isSuccess}
          onSave={save}
        />
      </div>
    </Card>
  );
}

function DeliveryCard({ settings, canEdit }: { settings: CompanySettings; canEdit: boolean }) {
  const mutation = useUpdateSettings();

  const [liveLag, setLiveLag] = useState(settings.delivery.liveLagSeconds);
  const [backfillLag, setBackfillLag] = useState(settings.delivery.backfillLagSeconds);

  useEffect(() => {
    setLiveLag(settings.delivery.liveLagSeconds);
    setBackfillLag(settings.delivery.backfillLagSeconds);
  }, [settings.delivery]);

  const invalid = backfillLag <= liveLag;

  return (
    <Card>
      <CardHeader
        title="Data delivery"
        hint="Where the line falls between a fix that was on the map in time and one that arrived after the fact."
      />

      <div className="space-y-4 p-4">
        <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Both thresholds measure <span className="font-medium text-foreground">lag</span>: the
          time between the phone capturing a fix and this server storing it. That is deliberately
          not the same as the phone&rsquo;s own &ldquo;I had no signal&rdquo; flag — a captive
          portal, an expired sign-in or this server being down all produce points the device
          believed it sent live. Lag is measured at both ends and cannot be wrong about it.
        </p>

        <Field
          label={`Live — up to ${formatDuration(liveLag)}`}
          hint="At or under this, the fix was on the live map while it was still true. The device's own upload tick is 60 seconds, so anything under a couple of minutes is an ordinary healthy feed."
        >
          <input
            type="range"
            min={30}
            max={900}
            step={30}
            value={liveLag}
            disabled={!canEdit}
            onChange={(e) => setLiveLag(Number(e.target.value))}
            className="h-1 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary disabled:opacity-60"
          />
        </Field>

        <Field
          label={`Backfill — from ${formatDuration(backfillLag)}`}
          hint="At or over this, the map was materially wrong while it lasted: someone looking at it would have seen this employee somewhere they were not. Between the two thresholds a fix counts as merely delayed."
        >
          <input
            type="range"
            min={120}
            max={7200}
            step={60}
            value={backfillLag}
            disabled={!canEdit}
            onChange={(e) => setBackfillLag(Number(e.target.value))}
            className="h-1 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary disabled:opacity-60"
          />
        </Field>

        {invalid ? (
          <p className="text-xs text-danger">
            The backfill threshold has to be longer than the live one.
          </p>
        ) : null}

        <SaveRow
          canEdit={canEdit && !invalid}
          pending={mutation.isPending}
          error={mutation.error}
          saved={mutation.isSuccess}
          onSave={() =>
            mutation.mutate({
              delivery: { liveLagSeconds: liveLag, backfillLagSeconds: backfillLag },
            })
          }
        />
      </div>
    </Card>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      {hint ? <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span> : null}
      <span className="mt-2 block">{children}</span>
    </label>
  );
}

function SaveRow({
  canEdit,
  pending,
  error,
  saved,
  onSave,
}: {
  canEdit: boolean;
  pending: boolean;
  error: unknown;
  saved: boolean;
  onSave: () => void;
}) {
  return (
    <div className="flex items-center gap-3 border-t border-border pt-3">
      <button
        type="button"
        onClick={onSave}
        disabled={!canEdit || pending}
        className={cn(
          'rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity',
          !canEdit || pending ? 'opacity-50' : 'hover:opacity-90',
        )}
      >
        {pending ? 'Saving…' : 'Save'}
      </button>
      {error ? (
        <span className="text-xs text-danger">
          {error instanceof Error ? error.message : 'Could not save'}
        </span>
      ) : saved ? (
        <span className="text-xs text-success">Saved</span>
      ) : null}
    </div>
  );
}
